'use client';

import React from 'react';
import { ArrowUpDown, MessageSquare, Phone, MoreVertical } from 'lucide-react';
import { ActionMenu } from '@/components/ui/ActionMenu';

export interface ClientRecord {
  id: string;
  name: string;
  phone: string;
  email: string;
  dni?: string;
  notes?: string;
  points: number;
  tags: string[];
  totalSpent: number;
  appointmentsCount: number;
  createdAt: string;
  subProfiles?: any[];
  recentAppointments?: any[];
}

interface ClientsTableProps {
  clients: ClientRecord[];
  loading: boolean;
  selectedClientId: string | null;
  onSelectClient: (clientId: string) => void;
  onOpenDetail: (client: ClientRecord) => void;
  onEditClient?: (client: ClientRecord) => void;
}

export default function ClientsTable({
  clients,
  loading,
  selectedClientId,
  onSelectClient,
  onOpenDetail,
  onEditClient
}: ClientsTableProps) {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200/90 rounded-2xl p-16 text-center shadow-xs">
        <div className="w-8 h-8 border-2 border-[#6255F5] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-500">Cargando base de clientes...</p>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="bg-white border border-slate-200/90 rounded-2xl p-16 text-center shadow-xs">
        <p className="text-base font-bold text-slate-700">No se encontraron clientes.</p>
        <p className="text-xs text-slate-400 mt-1">Modificá los filtros de búsqueda o registrá un nuevo cliente.</p>
      </div>
    );
  }

  // Generar iniciales y colores de fondo
  const getAvatarInfo = (name: string) => {
    const parts = name.trim().split(' ');
    const initials = parts.length > 1 
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();

    const colors = [
      'bg-violet-100 text-[#6255F5]',
      'bg-rose-100 text-rose-700',
      'bg-emerald-100 text-emerald-700',
      'bg-amber-100 text-amber-700',
      'bg-sky-100 text-sky-700',
    ];
    const index = (name.charCodeAt(0) || 0) % colors.length;
    return { initials, colorClass: colors[index] };
  };

  const openWhatsApp = (phone: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/549${cleanPhone}`, '_blank');
  };

  return (
    <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs text-slate-600">
          <thead className="bg-slate-50/80 border-b border-slate-200/80 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="py-3 px-4 w-10">
                <input 
                  type="checkbox" 
                  aria-label="Seleccionar todos"
                  className="rounded text-[#6255F5] focus:ring-[#6255F5] border-slate-300" 
                />
              </th>
              <th className="py-3 px-4">
                <div className="flex items-center gap-1 cursor-pointer">
                  <span>Cliente</span>
                  <ArrowUpDown className="w-3 h-3 text-slate-400" />
                </div>
              </th>
              <th className="py-3 px-4">Contacto Directo</th>
              <th className="py-3 px-4 text-center">Citas</th>
              <th className="py-3 px-4 text-right">LTV Gastado</th>
              <th className="py-3 px-4 text-center">Puntos</th>
              <th className="py-3 px-4 text-right">Alta</th>
              <th className="py-3 px-4 w-12 text-center"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {clients.map((client) => {
              const { initials, colorClass } = getAvatarInfo(client.name);
              const isSelected = selectedClientId === client.id;
              const hasVisits = client.appointmentsCount > 0;
              const createdDate = new Date(client.createdAt).toLocaleDateString('es-AR', {
                day: '2-digit',
                month: 'short',
                year: '2-digit'
              });

              return (
                <tr
                  key={client.id}
                  onClick={() => onOpenDetail(client)}
                  className={`hover:bg-slate-50/80 transition cursor-pointer group ${
                    isSelected ? 'bg-violet-50/30' : ''
                  }`}
                >
                  {/* Checkbox */}
                  <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      aria-label={`Seleccionar a ${client.name}`}
                      onChange={() => onSelectClient(client.id)}
                      className="rounded text-[#6255F5] focus:ring-[#6255F5] border-slate-300"
                    />
                  </td>

                  {/* Cliente (Avatar + Nombre + Tags) */}
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 ${colorClass}`}>
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 group-hover:text-[#6255F5] transition truncate">
                          {client.name}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {client.tags && client.tags.length > 0 ? (
                            client.tags.slice(0, 2).map((t) => (
                              <span
                                key={t}
                                className="px-1.5 py-0.2 text-[9px] font-bold rounded bg-slate-100 text-slate-600"
                              >
                                {t}
                              </span>
                            ))
                          ) : (
                            <span className="text-[11px] text-slate-400">Sin etiquetas</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Contacto Directo: Teléfono + Botón WhatsApp */}
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-700">
                        +54 {client.phone}
                      </span>
                      <button
                        onClick={(e) => openWhatsApp(client.phone, e)}
                        title="Abrir chat en WhatsApp"
                        className="p-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition cursor-pointer"
                      >
                        <MessageSquare className="w-3.5 h-3.5 fill-emerald-600" />
                      </button>
                    </div>
                  </td>

                  {/* Visitas / Citas */}
                  <td className="py-3.5 px-4 text-center font-bold text-slate-800">
                    {hasVisits ? (
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px]">
                        {client.appointmentsCount}
                      </span>
                    ) : (
                      <span className="text-slate-400 font-normal">0</span>
                    )}
                  </td>

                  {/* Gastado (LTV) */}
                  <td className="py-3.5 px-4 text-right font-black text-slate-900">
                    {client.totalSpent > 0 ? (
                      `$${client.totalSpent.toLocaleString('es-AR')}`
                    ) : (
                      <span className="text-slate-400 font-normal">$0</span>
                    )}
                  </td>

                  {/* Puntos */}
                  <td className="py-3.5 px-4 text-center font-semibold text-[#6255F5]">
                    {client.points || '0'}
                  </td>

                  {/* Alta */}
                  <td className="py-3.5 px-4 text-right font-mono text-slate-400">
                    {createdDate}
                  </td>

                  {/* Menú de Acciones */}
                  <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                    <ActionMenu
                      title="Acciones del cliente"
                      align="right"
                      items={[
                        {
                          label: 'Ver Ficha 360°',
                          onClick: () => onOpenDetail(client),
                        },
                        {
                          label: 'Abrir WhatsApp',
                          onClick: () => {
                            const cleanPhone = client.phone.replace(/[^0-9]/g, '');
                            window.open(`https://wa.me/549${cleanPhone}`, '_blank');
                          },
                        },
                        ...(onEditClient
                          ? [
                              {
                                label: 'Editar Datos',
                                onClick: () => onEditClient(client),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
